import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Calendar, MapPin, ExternalLink, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { buildPaystackCallbackUrl } from "@/lib/payments";
import { sendOwnerPush } from "@/lib/owner-push";

interface MediaItem {
  url: string;
  type: "image" | "video";
}

function googleMapsUrl(d: any) {
  const placeId = d?.venue_place_id;
  const lat = d?.venue_lat;
  const lng = d?.venue_lng;
  const address = d?.venue_address;
  if (placeId) return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(placeId)}`;
  if (typeof lat === "number" && typeof lng === "number") return `https://www.google.com/maps?q=${lat},${lng}`;
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return null;
}

// Currency helper
const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    GHS: "₵",
    NGN: "₦",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

// Helper function to get media items from an event
const getEventMediaItems = (event: any): MediaItem[] => {
  // Try to get from media_items column first
  const mediaItems = event?.media_items;
  if (Array.isArray(mediaItems) && mediaItems.length > 0) {
    return mediaItems.map((item: any) => ({
      url: item.url,
      type: item.type || (() => {
        const url = (item.url || '').toLowerCase();
        return url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv')
          ? 'video' : 'image';
      })(),
    }));
  }
  
  // Fallback to cover_image_url for backward compatibility
  if (event?.cover_image_url) {
    const url = event.cover_image_url.toLowerCase();
    const isVideo = url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov') || url.endsWith('.avi') || url.endsWith('.mkv');
    return [{
      url: event.cover_image_url,
      type: isVideo ? 'video' : 'image',
    }];
  }
  
  return [];
};

export default function EventPage() {
  const { businessSlug, eventSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState(false);
  const [startingPayment, setStartingPayment] = useState(false);
  const [selectedTicketTypeId, setSelectedTicketTypeId] = useState<string>("");
  const [reg, setReg] = useState({ name: "", email: "", phone: "", quantity: "1" });
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const videoRefs = useRef<Map<number, HTMLVideoElement>>(new Map());
  const imageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!businessSlug || !eventSlug) return;
      setLoading(true);
      setError(null);
      try {
      const [{ data, error }, { data: ttData, error: ttErr }] = await Promise.all([
        supabase.rpc("get_public_event" as any, {
          _business_slug: businessSlug,
          _event_slug: eventSlug,
        }),
        supabase.rpc("get_public_event_ticket_types" as any, {
          _business_slug: businessSlug,
          _event_slug: eventSlug,
        }),
      ]);
        if (error) {
          setError(error.message);
        }
      setData((data && data[0]) || null);
        
        // Handle ticket types - RPC returns array directly
        const ticketTypesArray = ttErr ? [] : (Array.isArray(ttData) ? ttData : []);
        setTicketTypes(ticketTypesArray);
        
        // Set first ticket type as selected if available
        if (ticketTypesArray.length > 0 && ticketTypesArray[0]?.id) {
          setSelectedTicketTypeId(String(ticketTypesArray[0].id));
        } else {
          setSelectedTicketTypeId("");
        }
      } catch (err: any) {
        setError(err?.message || "Failed to load event");
      } finally {
      setLoading(false);
      }
    };
    run();
  }, [businessSlug, eventSlug]);

  // All hooks must be called before any conditional returns
  const hasTicketTypes = (ticketTypes || []).length > 0;
  const selectedTicket = useMemo(() => {
    if (!hasTicketTypes) return null;
    return (ticketTypes || []).find((t: any) => String(t.id) === String(selectedTicketTypeId)) || null;
  }, [hasTicketTypes, ticketTypes, selectedTicketTypeId]);

  // Get media items
  const mediaItems = useMemo(() => getEventMediaItems(data), [data]);
  const hasMultipleMedia = mediaItems.length > 1;

  // Auto-advance carousel
  useEffect(() => {
    if (mediaItems.length <= 1) return;

    const currentMedia = mediaItems[currentMediaIndex];
    if (!currentMedia) return;

    // Clear any existing timeout
    if (imageTimeoutRef.current) {
      clearTimeout(imageTimeoutRef.current);
    }

    if (currentMedia.type === 'image') {
      // Images: show for 7 seconds
      imageTimeoutRef.current = setTimeout(() => {
        setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
      }, 7000);
    }
    // Videos handle their own transition via onEnded event

    return () => {
      if (imageTimeoutRef.current) {
        clearTimeout(imageTimeoutRef.current);
      }
    };
  }, [currentMediaIndex, mediaItems]);

  // Handle video end events
  const handleVideoEnd = (index: number) => {
    if (hasMultipleMedia) {
      setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
    }
  };

  // Compute derived values (these are not hooks, so they can be after useMemo)
  const start = data?.starts_at ? format(new Date(data.starts_at), "MMM d, yyyy • h:mm a") : "";
  const end = data?.ends_at ? format(new Date(data.ends_at), "MMM d, yyyy • h:mm a") : null;
  const maps = data ? googleMapsUrl(data) : null;
  const seatsLeft = data && typeof data.seats_left === "number" ? data.seats_left : null;
  const isSoldOut = seatsLeft !== null && seatsLeft <= 0;
  const currency = data?.currency || data?.business_currency || "GHS";
  const basePrice = data?.price_amount == null ? null : Number(data.price_amount);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background p-6">
        <p className="text-sm text-muted-foreground">This event is not available (it may be draft or unpublished).</p>
      </div>
    );
  }

  const unitCurrency = (selectedTicket?.currency || currency) as string;
  const unitPrice = selectedTicket?.price_amount == null ? basePrice : Number(selectedTicket.price_amount);
  const usesOnlineCheckout =
    data?.collect_booking_deposits === true || data?.paystack_connected_account_enabled === true;
  const directPaymentInstructions = String(data?.booking_deposit_payment_instructions || "").trim();

  const submit = async () => {
    if (!businessSlug || !eventSlug) return;
    if (!reg.name.trim()) return;
    if (isSoldOut) return;
    if (hasTicketTypes && !selectedTicketTypeId) return;
    
    const qty = Math.max(1, Number(reg.quantity || 1));
    const totalAmount = unitPrice != null ? unitPrice * qty : 0;
    
    // If there's a price, create pending registration and either initiate checkout or show payment instructions.
    if (totalAmount > 0) {
      if (usesOnlineCheckout && !reg.email.trim()) {
        alert("Email is required for paid events.");
        return;
      }

      if (!usesOnlineCheckout && !directPaymentInstructions) {
        alert("Payment instructions are not available for this event yet. Please contact the business.");
        return;
      }
      
      setStartingPayment(true);
      try {
        // Create pending registration first
        const { data: res, error: regError } = await supabase.rpc("register_for_event" as any, {
          _business_slug: businessSlug,
          _event_slug: eventSlug,
          _name: reg.name.trim(),
          _email: reg.email.trim(),
          _phone: reg.phone.trim() || null,
          _quantity: qty,
          _ticket_type_id: hasTicketTypes ? selectedTicketTypeId : null,
          _status: "pending",
        });
        if (regError) throw regError;
        const registrationId = res?.[0]?.registration_id as string | undefined;
        if (!registrationId) throw new Error("Failed to create registration");

        if (!usesOnlineCheckout) {
          void sendOwnerPush({
            businessSlug,
            title: "New event registration pending payment",
            body: `A guest registered for ${data.title} and needs to pay directly.`,
            url: "/app/events",
            data: {
              context: "event_registration_pending_payment",
              context_id: registrationId,
            },
          });

          navigate(`/events/${businessSlug}/${eventSlug}/ticket/${registrationId}?payment=pending`);
          return;
        }

        // Initialize payment - redirect to ticket page after payment
        const ticketPath = `/events/${businessSlug}/${eventSlug}/ticket/${registrationId}`;
        const callbackUrl = buildPaystackCallbackUrl(ticketPath);
        const { data: payData, error: payError } = await supabase.functions.invoke("paystack-init", {
          body: {
            context: { type: "event_registration", registrationId },
            payerEmail: reg.email.trim(),
            payerName: reg.name.trim() || null,
            callbackUrl,
          },
        });
        if (payError) throw payError;
        const url = String((payData as any)?.authorizationUrl || "");
        if (!url) throw new Error("Missing Paystack authorizationUrl");
        
        // Redirect to Paystack
        setTimeout(() => {
          window.location.assign(url);
        }, 50);
      } catch (e: any) {
        alert(e?.message || "Failed to start payment.");
        setStartingPayment(false);
      }
      return;
    }
    
    // Free event - register immediately
    setRegistering(true);
    try {
      const { data: res, error } = await supabase.rpc("register_for_event" as any, {
        _business_slug: businessSlug,
        _event_slug: eventSlug,
        _name: reg.name.trim(),
        _email: reg.email.trim() || null,
        _phone: reg.phone.trim() || null,
        _quantity: qty,
        _ticket_type_id: hasTicketTypes ? selectedTicketTypeId : null,
      });
      if (error) throw error;
      const registrationId = res?.[0]?.registration_id as string | undefined;
      if (!registrationId) throw new Error("Failed to create registration");

      // Send confirmation email (best-effort; non-blocking)
        supabase.functions
          .invoke("send-email", { body: { type: "event_registration", registrationId } })
          .catch(() => {});

      void sendOwnerPush({
        businessSlug,
        title: "New event registration",
        body: `A guest just registered for ${data.title}.`,
        url: "/app/events",
        data: {
          context: "event_registration_created",
          context_id: registrationId,
        },
      });

      // Redirect to ticket page
      navigate(`/events/${businessSlug}/${eventSlug}/ticket/${registrationId}`);
    } catch (e: any) {
      alert(e?.message || "Failed to register.");
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl p-4 sm:p-8">
        <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-soft">
          {mediaItems.length > 0 ? (
            <div className="relative w-full overflow-hidden bg-black aspect-[4/5] sm:aspect-[16/9]">
              {mediaItems.map((item, index) => {
                const isActive = index === currentMediaIndex;
                return (
                  <div
                    key={`${item.url}-${index}`}
                    className={cn(
                      "absolute inset-0 transition-opacity duration-500 ease-in-out",
                      isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
                    )}
                  >
                    {item.type === 'video' ? (
                      <video
                        ref={(el) => {
                          if (el) {
                            videoRefs.current.set(index, el);
                            if (isActive) {
                              el.currentTime = 0; // Reset video to start
                              el.play().catch(() => {
                                // Auto-play might fail, that's okay
                              });
                            } else {
                              el.pause();
                            }
                          } else {
                            videoRefs.current.delete(index);
                          }
                        }}
                        src={item.url}
                        className="w-full h-full object-cover object-center"
                        muted
                        playsInline
                        loop={!hasMultipleMedia}
                        autoPlay={isActive}
                        onEnded={() => handleVideoEnd(index)}
                        onLoadedData={() => {
                          if (isActive) {
                            const video = videoRefs.current.get(index);
                            if (video && video.paused) {
                              video.play().catch(() => {
                                // Auto-play might fail, that's okay
                              });
                            }
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={`${data.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover object-center"
                        loading={isActive ? "eager" : "lazy"}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-64 w-full bg-gradient-to-br from-primary/15 via-primary/5 to-background flex items-center justify-center">
              <Calendar className="h-12 w-12 text-primary" />
            </div>
          )}

          <div className="p-6">
            <p className="text-sm text-muted-foreground">{data.business_name}</p>
            <h1 className="mt-2 text-3xl font-bold text-foreground">{data.title}</h1>

            <div className="mt-4 grid gap-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{start}{end ? ` – ${end}` : ""}</span>
              </div>
              {(data.venue_name || data.venue_address) ? (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5" />
                  <div>
                    {data.venue_name ? <div className="text-foreground">{data.venue_name}</div> : null}
                    {data.venue_address ? <div>{data.venue_address}</div> : null}
                  {maps ? (
                    <a
                      className="inline-flex items-center gap-2 text-primary text-sm mt-2 hover:underline"
                      href={maps}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open in Google Maps
                    </a>
                  ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            {data.description ? (
              <div className="mt-6 prose prose-sm max-w-none text-foreground">
                <p>{data.description}</p>
              </div>
            ) : null}

            <div className="mt-8">
              <p className="font-semibold text-foreground">Register</p>
              <p className="text-sm text-muted-foreground mt-1">
                {isSoldOut ? "Sold out." : "Get your ticket code instantly."}
                {!hasTicketTypes && unitPrice != null && unitPrice > 0
                  ? ` • ${unitCurrency} ${unitPrice.toFixed(2)} per ticket`
                  : ""}
              </p>
              {unitPrice != null && unitPrice > 0 ? (
                <p className="mt-2 rounded-lg border border-border bg-muted/25 px-3 py-2 text-xs text-muted-foreground">
                  {usesOnlineCheckout
                    ? "Payment is collected securely online after registration."
                    : "After registering, you will see the business payment instructions. Your ticket stays pending until payment is confirmed."}
                </p>
              ) : null}

              <div className="mt-4 grid gap-3">
                {hasTicketTypes && ticketTypes.length > 0 ? (
                  <div className="grid gap-2">
                    <Label>Ticket type</Label>
                    <div className="grid gap-3">
                      {ticketTypes.map((t: any) => {
                        const isSelected = String(t.id) === String(selectedTicketTypeId);
                        const ticketPrice = Number(t.price_amount || 0);
                        const ticketCurrency = t.currency || unitCurrency;
                        const durationText = `${t.duration_value} ${t.duration_unit}`;
                        
                        return (
                          <button
                            key={String(t.id)}
                            type="button"
                            onClick={() => setSelectedTicketTypeId(String(t.id))}
                            className={cn(
                              "flex items-start gap-4 p-4 rounded-xl border text-left transition-all",
                              isSelected
                                ? "border-primary bg-primary/10"
                                : "border-border hover:border-primary/50 hover:bg-primary/5"
                            )}
                          >
                            <div className="flex-shrink-0 mt-0.5">
                              {isSelected ? (
                                <CheckCircle className="h-5 w-5 text-primary" />
                              ) : (
                                <div className="h-5 w-5 rounded-full border-2 border-border" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-foreground">{t.name}</h3>
                              {t.description && (
                                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{t.description}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" />
                                  {durationText}
                                </span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              {ticketPrice > 0 ? (
                                <p className="font-semibold text-foreground">
                                  {getCurrencySymbol(ticketCurrency)}
                                  {ticketPrice.toFixed(2)}
                                </p>
                              ) : (
                                <p className="text-xs font-medium text-muted-foreground">
                                  Custom pricing
                                </p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div className="grid gap-2">
                  <Label>Name</Label>
                  <Input value={reg.name} onChange={(e) => setReg((r) => ({ ...r, name: e.target.value }))} placeholder="Your name" />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="grid gap-2">
                    <Label>Email{unitPrice != null && unitPrice > 0 ? " *" : " (optional)"}</Label>
                    <Input 
                      type="email"
                      value={reg.email} 
                      onChange={(e) => setReg((r) => ({ ...r, email: e.target.value }))} 
                      placeholder="you@email.com"
                      required={unitPrice != null && unitPrice > 0}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Phone (optional)</Label>
                    <Input value={reg.phone} onChange={(e) => setReg((r) => ({ ...r, phone: e.target.value }))} placeholder="+233…" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div className="grid gap-2">
                    <Label>Quantity</Label>
                    <Input type="number" min={1} value={reg.quantity} onChange={(e) => setReg((r) => ({ ...r, quantity: e.target.value }))} />
                  </div>
                  <Button 
                    className="w-full sm:w-auto" 
                    onClick={submit} 
                    disabled={registering || startingPayment || !reg.name.trim() || isSoldOut || (usesOnlineCheckout && unitPrice != null && unitPrice > 0 && !reg.email.trim())}
                  >
                    {startingPayment ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirecting to payment...
                      </>
                    ) : registering ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      isSoldOut
                        ? "Sold out"
                        : unitPrice != null && unitPrice > 0
                          ? usesOnlineCheckout
                            ? "Pay & Register"
                            : "Register & view payment instructions"
                          : "Register"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


